// ═══════════════════════════════════════════════════════════════════════════
// DYN-5 — constraint-based stretch fallback for action-less dynamic blocks.
//
// WHAT THIS DOES
// ──────────────
// Dimensional constraint parameters (constraint-horizontal /
// constraint-vertical) in AutoCAD drive geometry through a constraint graph
// — the solver pins endpoints of constrained entities and propagates the
// current distance to every touching primitive. Our first-generation
// implementation (DYN-2 Batch C) assumed every constraint-horizontal /
// constraint-vertical parameter had a Move / Stretch / Scale ACTION linked
// to it — if a block is purely constraint-driven (no actions), DYNSET used
// to write the current value into customProperties but the geometry
// rendered at default.
//
// That is exactly the case with the "Двутавр ГОСТ 8239-89" ibeam and any
// similar GOST profile block authored around AutoCAD 2012+ dynamic
// constraints. DYN-5 closes this gap with a pragmatic heuristic: for each
// constraint-horizontal / constraint-vertical parameter whose current
// distance diverges from the default, apply an axial stretch anchored at
// the parameter's base point (pointA) that scales every vertex whose
// projection on the driving axis lies beyond that anchor.
//
// AUTOCAD PARITY CAVEAT
// ─────────────────────
// This is NOT a full Gauss-Newton constraint solver. The real AutoCAD
// behavior for constraint-based blocks combines dimensional + geometric
// constraints (coincident, parallel, perpendicular) in a non-linear system
// and solves for all degrees of freedom at once. Our heuristic is a single
// axial scale pass that produces identical output for the large class of
// "linear profile" blocks (beams, channels, angles, L-profiles): geometry
// with one anchor point and most vertices extending along the driving
// axis. A follow-up (future DYN task) will wire `solveBlockConstraints`
// (already present in constraintSolverIntegration.ts) into the full BPT-
// driven evaluation path.
//
// SKIPPING ACTION-DRIVEN PARAMETERS
// ─────────────────────────────────
// Blocks that DO have a Move / Stretch / Scale / Array action linked to
// a constraint parameter keep their existing semantics — the action pass
// owns the transformation and the constraint stretch would double-apply.
// We detect "action-driven" parameters via the input metadata.actions[*]
// .parameterId set and skip those here.
// ═══════════════════════════════════════════════════════════════════════════

import type {
  DynamicAction,
  DynamicParameter,
  HorizontalConstraintParameter,
  VerticalConstraintParameter,
} from "@dxf-ai-editor/drawing-model";

import type { EntityLike } from "./geometry";

export interface ConstraintStretchInput {
  readonly parameters: ReadonlyArray<DynamicParameter>;
  /**
   * Pre-instance-overlay parameters — carries the "authored" distance the
   * block was designed with. Used as the stretch baseline because the
   * post-overlay `parameters` have `currentDistance` already replaced by
   * the instance's customProperties value (that's our stretch TARGET,
   * not the baseline).
   *
   * Optional for backward compatibility; if omitted, falls back to
   * `defaultDistance` on the post-overlay param, which works for
   * synthetic round-trip fixtures (default == authored == 0-safe).
   */
  readonly sourceParameters?: ReadonlyArray<DynamicParameter>;
  readonly actions: ReadonlyArray<DynamicAction>;
  readonly entities: ReadonlyArray<EntityLike & { id: string }>;
}

export interface ConstraintStretchResult {
  readonly entities: ReadonlyArray<EntityLike & { id: string }>;
  /** Number of parameters that actually drove a stretch (for diagnostics). */
  readonly appliedCount: number;
}

/**
 * Apply constraint-based stretching to entities for every unreferenced
 * constraint-horizontal / constraint-vertical parameter whose runtime
 * distance differs from its default.
 *
 * Returns a new list of entities (shallow clones where modified, references
 * kept where untouched).
 */
export function applyConstraintStretch(
  input: ConstraintStretchInput,
): ConstraintStretchResult {
  const actionDrivenParamIds = new Set<string>();
  for (const action of input.actions) {
    actionDrivenParamIds.add(action.parameterId);
  }

  let entities = input.entities;
  let appliedCount = 0;

  const sourceById = new Map<string, DynamicParameter>();
  for (const sp of input.sourceParameters ?? []) sourceById.set(sp.id, sp);

  // DYN-5 debug — telemetry on window for browser inspection.
  const debug: Array<{ name: string; kind: string; target: number; baseline: number; scale: number; anchor: number; applied: boolean; skipReason?: string }> = [];
  const gw = (typeof globalThis !== "undefined" ? globalThis : {}) as {
    __DYN5_STRETCH__?: typeof debug;
    __DYN5_INCOMING_PARAMS__?: Array<{ name: string; kind: string; currentDistance?: number }>;
  };
  gw.__DYN5_STRETCH__ = debug;
  gw.__DYN5_INCOMING_PARAMS__ = input.parameters.map((p) => ({
    name: p.name,
    kind: p.kind,
    currentDistance: (p as { currentDistance?: number }).currentDistance,
  }));

  for (const param of input.parameters) {
    if (
      param.kind !== "constraint-horizontal" &&
      param.kind !== "constraint-vertical"
    ) {
      debug.push({ name: param.name, kind: param.kind, target: 0, baseline: 0, scale: 0, anchor: 0, applied: false, skipReason: "kind-filter" });
      continue;
    }
    if (actionDrivenParamIds.has(param.id)) {
      debug.push({ name: param.name, kind: param.kind, target: 0, baseline: 0, scale: 0, anchor: 0, applied: false, skipReason: "action-driven" });
      continue;
    }

    const cp = param as
      | HorizontalConstraintParameter
      | VerticalConstraintParameter;

    // Post-overlay currentDistance = stretch TARGET (instance override
    // or authored value when no DYNSET set).
    const target = cp.currentDistance ?? 0;

    // Guard: initCustomPropertiesFromMetadata seeds constraint params with
    // abs(defaultDistance) which is 0 for real AutoCAD 2018+ blocks. That
    // means the INSTANCE's customProperties[name] is 0 for every
    // unconfigured constraint → applyInstanceParameters overwrites
    // param.currentDistance with 0 → our scale = 0/baseline = 0 and
    // geometry collapses. Skip when target is exactly 0 (constraint
    // distances are physical lengths — 0 is a meaningless value the
    // user would never author via DYNSET).
    if (target === 0) {
      debug.push({ name: param.name, kind: param.kind, target, baseline: 0, scale: 0, anchor: 0, applied: false, skipReason: "target=0 (seeded placeholder)" });
      continue;
    }

    // Baseline resolution (priority order):
    //   1. pre-overlay currentDistance (source meta) IF it's close to the
    //      authored entity extent — matches Batch-C synthetic fixtures
    //      where param.currentDistance == entity length
    //   2. measured entity extent along the stretch axis — matches real
    //      AutoCAD 2018+ constraint-based blocks where param.currentDistance
    //      is an internal constraint-solver value (e.g. 39.31) unrelated to
    //      actual entity length (e.g. 200mm)
    //   3. defaultDistance fallback
    const sourceParam = sourceById.get(param.id);
    const sourceCurrent =
      sourceParam && (
        sourceParam.kind === "constraint-horizontal" ||
        sourceParam.kind === "constraint-vertical"
      )
        ? (sourceParam as HorizontalConstraintParameter | VerticalConstraintParameter).currentDistance
        : undefined;

    const axis: "x" | "y" =
      param.kind === "constraint-horizontal" ? "x" : "y";
    const anchor = axis === "x" ? cp.pointA.x : cp.pointA.y;

    // Measure maximum entity extent beyond the anchor along the driving axis.
    let measuredExtent = 0;
    for (const e of entities) {
      const coords = extractAxisCoords(e, axis);
      for (const c of coords) {
        if (c > anchor) {
          const d = c - anchor;
          if (d > measuredExtent) measuredExtent = d;
        }
      }
    }

    const defaultVal = cp.defaultDistance ?? 0;
    // Choose baseline: if sourceCurrent agrees with measuredExtent within
    // an order of magnitude (synthetic fixture case), use sourceCurrent;
    // else fall back to measuredExtent (real АБИ1 case where param value
    // is a constraint-solver scalar, not the physical length).
    let baseline = 0;
    if (
      sourceCurrent !== undefined &&
      Math.abs(sourceCurrent) > 1e-9 &&
      measuredExtent > 0 &&
      Math.abs(sourceCurrent - measuredExtent) / measuredExtent < 0.5
    ) {
      baseline = sourceCurrent;
    } else if (measuredExtent > 0) {
      baseline = measuredExtent;
    } else if (Math.abs(defaultVal) > 1e-9) {
      baseline = defaultVal;
    }

    // No stretch requested — target matches baseline (or baseline is 0/unknown).
    if (Math.abs(baseline) < 1e-9) continue;
    if (Math.abs(target - baseline) < 1e-9) continue;

    const scale = target / baseline;
    if (!Number.isFinite(scale)) {
      debug.push({ name: param.name, kind: param.kind, target, baseline, scale, anchor, applied: false, skipReason: "scale not finite" });
      continue;
    }

    entities = entities.map((e) => applyAxialStretch(e, axis, anchor, scale));
    appliedCount += 1;
    debug.push({ name: param.name, kind: param.kind, target, baseline, scale, anchor, applied: true });
  }

  return { entities, appliedCount };
}

/**
 * Collect every X or Y coordinate that defines an entity's extent.
 * Used to measure the authored physical length for constraint-based
 * stretch when the parameter's `currentDistance` doesn't map directly
 * to a geometry dimension.
 */
function extractAxisCoords(
  entity: EntityLike,
  axis: "x" | "y",
): readonly number[] {
  const out: number[] = [];
  switch (entity.type) {
    case "Line": {
      const e = entity as unknown as {
        start: { x: number; y: number };
        end: { x: number; y: number };
      };
      out.push(e.start[axis], e.end[axis]);
      break;
    }
    case "Circle":
    case "Arc":
    case "Ellipse": {
      const e = entity as unknown as {
        center: { x: number; y: number };
        radius?: number;
      };
      const r = e.radius ?? 0;
      out.push(e.center[axis] + r, e.center[axis] - r, e.center[axis]);
      break;
    }
    case "LWPOLYLINE": {
      const e = entity as unknown as {
        vertices: ReadonlyArray<{ x: number; y: number }>;
      };
      for (const v of e.vertices) out.push(v[axis]);
      break;
    }
    case "Text":
    case "MText":
    case "Attdef":
    case "Block": {
      const e = entity as unknown as { insertionPoint: { x: number; y: number } };
      out.push(e.insertionPoint[axis]);
      break;
    }
    default:
      break;
  }
  return out;
}

/**
 * For a single entity, stretch every "defining point" along `axis` so
 * that points beyond the anchor scale by `scale` relative to the anchor
 * (points at or below the anchor stay put).
 *
 * Stretched types: Line (start/end), Circle/Arc/Ellipse (center),
 * LWPOLYLINE (per-vertex). Unknown types pass through unchanged.
 *
 * Annotation entities (Text, MText, Attdef) and nested Block INSERTs are
 * intentionally NOT stretched — AutoCAD treats them as labels and nested
 * geometry with their own parameter graphs, which should survive a
 * parent-block DYNSET unchanged (matches Properties-palette behavior on
 * constraint-based parametric blocks). Without this exclusion, a Text
 * with insertionPoint.x=100 would move to x=6000 under scale=60, causing
 * labels to fly off-geometry on real АБИ1 ibeam layouts.
 *
 * For Circle / Arc / Ellipse we stretch the center only — non-uniform
 * axial stretch of a circle should conceptually produce an ellipse, but
 * matching AutoCAD's constraint-based block behavior we keep radii
 * intact. This matches how `scaleEntity` handles non-uniform scale.
 *
 * For LWPOLYLINE we stretch per-vertex, which is the correct behavior for
 * profile outlines.
 */
export function applyAxialStretch<T extends EntityLike>(
  entity: T,
  axis: "x" | "y",
  anchor: number,
  scale: number,
): T {
  if (scale === 1) return entity;

  switch (entity.type) {
    case "Line": {
      const e = entity as unknown as {
        start: { x: number; y: number };
        end: { x: number; y: number };
      };
      return {
        ...entity,
        start: stretchPointAxial(e.start, axis, anchor, scale),
        end: stretchPointAxial(e.end, axis, anchor, scale),
      };
    }
    case "Circle":
    case "Arc":
    case "Ellipse": {
      const e = entity as unknown as { center: { x: number; y: number } };
      return {
        ...entity,
        center: stretchPointAxial(e.center, axis, anchor, scale),
      };
    }
    case "LWPOLYLINE": {
      const e = entity as unknown as {
        vertices: ReadonlyArray<
          { x: number; y: number } & Record<string, unknown>
        >;
      };
      return {
        ...entity,
        vertices: e.vertices.map((v) => {
          const p = stretchPointAxial({ x: v.x, y: v.y }, axis, anchor, scale);
          return { ...v, x: p.x, y: p.y };
        }),
      };
    }
    default:
      return entity;
  }
}

/**
 * Stretch a single 2D point along one axis, anchored at `anchor`.
 * Points at or below `anchor` (on the driving axis) stay put; points
 * above scale linearly relative to the anchor.
 *
 * Formula (for scale s, anchor a, point p):
 *   p' = a + (p - a) * s   if p > a
 *   p' = p                 otherwise
 *
 * The inclusive-anchor branch (p === a stays put) matches AutoCAD's
 * "left/bottom edge is fixed" behavior. Points < anchor are rare in
 * constraint-based dynamic blocks (they would be on the wrong side of
 * the fixed edge) but they pass through unchanged to avoid inverting
 * the geometry.
 */
function stretchPointAxial(
  point: { x: number; y: number },
  axis: "x" | "y",
  anchor: number,
  scale: number,
): { x: number; y: number } {
  if (axis === "x") {
    if (point.x <= anchor) return point;
    return { x: anchor + (point.x - anchor) * scale, y: point.y };
  }
  if (point.y <= anchor) return point;
  return { x: point.x, y: anchor + (point.y - anchor) * scale };
}
